import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Flame, BarChart3, Crown } from "lucide-react";
import { api, media, fmtDate } from "@/lib/api";
import { NowHero } from "@/components/fo/NowHero";
import { ChartBoard } from "@/components/fo/ChartBoard";
import { ArtistCard } from "@/components/fo/ArtistCard";
import { SectionHead, Badges } from "@/components/fo/Bits";
import { EventCard } from "@/components/fo/Events";
import { Parallax, Reveal, CountUp } from "@/components/fo/Motion";

const Ticker = ({ totals, founding }) => {
  const items = [`${totals?.artists ?? 0} artists on air`, `${totals?.flames ?? 0} Flames given`, `${totals?.plays ?? 0} plays broadcast`, `${founding?.left ?? 0} Founding Artist spots left`, "1 in 3 tracks airs blind"];
  return (
    <div className="overflow-hidden border-y border-white/5 bg-[#0F1420] py-3">
      <div className="marquee flex w-max gap-12 whitespace-nowrap font-mono text-xs uppercase tracking-[.25em] text-slate-400">
        {[...items, ...items, ...items, ...items].map((t, i) => <span key={i} className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-[#FF2E63]" />{t}</span>)}
      </div>
    </div>
  );
};

const CoverArtist = ({ a }) => a && (
  <section className="mx-auto max-w-7xl px-4 py-24 md:px-8" data-testid="home-cover-artist">
    <div className="grid items-center gap-10 lg:grid-cols-2">
      <div className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-white/10" data-reveal>
        <Parallax speed={-0.12} className="absolute -inset-y-16 inset-x-0"><img src={media(a.photo)} alt={a.stage_name} className="h-full w-full object-cover" /></Parallax>
        <div className="absolute inset-0 bg-gradient-to-t from-[#07090E] to-transparent" />
        <span className="absolute left-5 top-5 font-mono text-[11px] uppercase tracking-[.3em] text-[#FFB800]">Cover artist</span>
      </div>
      <Reveal variant="right">
        <div className="label-mono mb-4">This week on the cover</div>
        <h2 className="font-display text-5xl font-black uppercase leading-none sm:text-7xl">{a.stage_name}</h2>
        <p className="mt-3 font-mono text-sm uppercase tracking-widest text-slate-500">{a.role_type} · {a.genre} · {a.city}</p>
        <Badges badges={a.badges} plan={a.plan} className="mt-5" />
        <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-300">{a.bio}</p>
        <Link to={`/a/${a.slug}`} className="btn-ghost mt-8" data-testid="cover-artist-link">Open artist page <ArrowRight className="h-4 w-4" /></Link>
      </Reveal>
    </div>
  </section>
);

const HOW = [
  [Eye, "Blind Drop", "One track in three airs with the artist name hidden. Listeners judge the music, not the follower count."],
  [Flame, "Flames & reactions", "Tap a Flame when a track hits. Tag what works: Voice, Beat, Hook, Lyrics, Vibe."],
  [BarChart3, "Two weekly charts", "Fan Chart rewards the crowd you bring. Blind Chart rewards the music alone. Reset every Monday."],
];

export default function Home() {
  const [d, setD] = useState(null);
  const [charts, setCharts] = useState(null);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/home").then((r) => setD(r.data));
    api.get("/charts").then((r) => setCharts(r.data));
    api.get("/events/upcoming").then((r) => setEvents(r.data));
  }, []);
  return (
    <div data-testid="home-page">
      <NowHero />
      <Ticker totals={d?.totals} founding={d?.founding} />
      <section className="mx-auto grid max-w-7xl gap-6 px-4 py-24 md:grid-cols-3 md:px-8">
        {HOW.map(([Icon, t, s], i) => (
          <div key={t} className={`panel fade-up d${i + 1} p-8`}>
            <Icon className="h-8 w-8 text-[#00F5D4]" />
            <h3 className="mt-6 font-display text-2xl font-bold">{t}</h3>
            <p className="mt-3 text-slate-400">{s}</p>
          </div>
        ))}
      </section>
      <section className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionHead label="Weekly Chart" title="Who is burning this week">Live ranking, updated with every Flame. Winners get interviews, home covers and free Pro months.</SectionHead>
        <ChartBoard data={charts} limit={5} />
        <Link to="/charts" className="btn-ghost mt-6" data-testid="home-charts-link">Full chart <ArrowRight className="h-4 w-4" /></Link>
      </section>
      {d?.aotm && (
        <section className="mx-auto max-w-7xl px-4 pt-24 md:px-8" data-testid="home-aotm">
          <Link to={`/a/${d.aotm.slug}`} className="panel console-corners relative grid items-center gap-8 overflow-hidden border-[#FFB800]/40 p-6 md:grid-cols-[260px_1fr] md:p-10">
            <div className="aspect-square overflow-hidden rounded-2xl"><img src={media(d.aotm.photo)} alt={d.aotm.stage_name} className="h-full w-full object-cover" /></div>
            <div>
              <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[.25em] text-[#FFB800]"><Crown className="h-4 w-4" />Artist of the Month · {d.aotm.label}</div>
              <h2 className="mt-4 font-display text-5xl font-black uppercase leading-none sm:text-6xl">{d.aotm.stage_name}</h2>
              <p className="mt-4 max-w-xl text-slate-300">{d.aotm.bio}</p>
              <p className="mt-4 font-mono text-xs uppercase tracking-widest text-slate-500">On air every night in prime time (20–23)</p>
            </div>
          </Link>
        </section>
      )}
      <CoverArtist a={d?.cover} />
      {events.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-24 md:px-8" data-testid="home-events">
          <SectionHead label="Live shows · next 30 days" title="On stage soon">Catch the Fuorionda artists live, near you.</SectionHead>
          <div className="no-scrollbar -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-2 md:-mx-8 md:px-8">{events.map((e) => <EventCard key={e.id} e={e} />)}</div>
        </section>
      )}
      <section className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="flex items-end justify-between"><SectionHead label="Showcase" title="Artists on air" /><Link to="/artists" className="mb-8 hidden text-sm text-[#00F5D4] sm:block" data-testid="home-artists-link">All artists →</Link></div>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">{d?.featured?.map((a, i) => <ArtistCard key={a.id} a={a} i={i} />)}</div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-24 md:px-8">
        <div className="panel console-corners relative overflow-hidden p-8 md:p-14">
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#FF2E63]/25 blur-[100px]" />
          <div className="label-mono mb-4">For artists</div>
          <h2 className="max-w-3xl font-display text-4xl font-black uppercase leading-none sm:text-5xl">Your music on air. Heard by strangers. Backed by real numbers.</h2>
          <p className="mt-5 max-w-xl text-slate-400">Free to join. The first 50 approved artists get 3 months of Pro and a permanent Founding Artist badge.</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/join" className="btn-hot" data-testid="home-join-cta">Claim my free spot</Link>
            <span className="led text-lg" data-testid="home-founding-left">{d?.founding ? <CountUp value={d.founding.left} from={d.founding.limit} /> : "--"}/{d?.founding?.limit ?? 50} spots left</span>
          </div>
        </div>
      </section>
      {d?.articles?.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-24 md:px-8">
          <SectionHead label="Magazine" title="Interviews, reviews, news" />
          <div className="grid gap-6 md:grid-cols-3">
            {d.articles.map((a) => (
              <Link key={a.id} to={`/blog/${a.slug}`} data-testid={`home-article-${a.slug}`} className="group panel overflow-hidden transition-[border-color] hover:border-[#00F5D4]/40">
                <div className="aspect-video overflow-hidden"><img src={media(a.cover)} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="p-6"><span className="label-mono">{a.kind} · {fmtDate(a.created_at)}</span><h3 className="mt-3 font-display text-xl font-bold leading-tight">{a.title}</h3></div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
